import { apiListProducts, apiLowStock, type Shop, type Variant } from '~/lib/api/shop';
import { classNames } from '~/utils/classNames';
import { CARD, LoadError, Section, SkeletonCard } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { StockControl } from './StockControl';
import { variantStockLabel, variantTitle } from './shop-model';

export function InventoryView({ appId, shop }: { appId: string; shop: Shop }) {
  const low = useResource(() => apiLowStock(appId), appId);
  // Low-stock rows are bare variants; the product list supplies their titles.
  const products = useResource(() => apiListProducts(appId), appId);
  const titles = new Map((products.data ?? []).map((d) => [d.product.id, d.product.title]));

  const onChanged = (next: Variant) =>
    low.setData((prev) =>
      (prev ?? [])
        .map((v) => (v.id === next.id ? next : v))
        // Restocked above the threshold, or untracked: no longer low.
        .filter((v) => v.stock !== null && v.stock <= shop.lowStockThreshold),
    );

  return (
    <Section title="Low stock" icon="i-ph:warning-diamond">
      <p className="text-[12px] leading-relaxed text-muted">
        Tracked variants at or below {shop.lowStockThreshold} on hand. Change the threshold in Settings. Untracked
        variants never run out.
      </p>

      {low.loading && !low.data ? (
        <SkeletonCard lines={3} />
      ) : low.error ? (
        <LoadError message="Could not load low stock." onRetry={() => void low.reload()} />
      ) : !low.data?.length ? (
        <div className={classNames(CARD, 'px-4 py-6 text-center')}>
          <div className="i-ph:check-circle mx-auto mb-2 text-xl text-faint" />
          <p className="text-[13px] text-primary">Nothing is running low</p>
        </div>
      ) : (
        <div className={classNames(CARD, 'divide-y divide-hairline overflow-hidden')}>
          {low.data.map((v) => {
            const stock = variantStockLabel(v);

            return (
              <div key={v.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[13px] text-primary">
                      {titles.get(v.productId) ?? (products.loading ? '…' : 'Product')}
                      {v.options.length > 0 && <span className="text-secondary"> · {variantTitle(v.options)}</span>}
                    </div>
                    <div className="font-geist-mono text-[10px] text-muted">{v.sku}</div>
                  </div>
                  <span
                    className={classNames(
                      'shrink-0 text-[11px]',
                      stock.tone === 'danger' ? 'text-danger' : 'text-warning',
                    )}
                  >
                    {stock.label}
                  </span>
                </div>
                <div className="mt-1.5">
                  <StockControl appId={appId} variant={v} onChanged={onChanged} defaultMode="delta" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}
